import { ClientGalleryOrderInformation, OrderInformationContext } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryOrderInformation'
import { FC, useMemo } from 'react'
import { GalleryAssignmentContextProvider, GalleryDealContextProvider } from 'components/pages/Gallery/_main/contexts'

import { AnalyticsEvent } from 'utils/analytics'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { Color } from 'constants/assets'
import { ClientPropertyPriceSummaryCard } from '../../ClientPropertyPriceSummaryCard'
import { EmptyContent } from 'components/common/EmptyContent'
import { GRAY_900 } from 'constants/styling/theme'
import Grid from '@mui/material/Grid'
import { MUIButton } from 'components/common/MUIButton'
import { RescheduleOrderModuleProvider } from 'components/common/RescheduleOrder'
import { ShoppingCartOutlined } from '@mui/icons-material'
import Stack from '@mui/material/Stack'
import { useClientProperty } from '../contexts'
import { useStartOrderForProperty } from '../useStartOrderForProperty'
import { useGetPropertyOrders } from 'dataQueries'
import { useTranslation } from 'react-i18next'
import { useCanUserOrder } from 'utils/user/canUserOrder'

export const ClientPropertyOrdersPage: FC = () => {
  const { t } = useTranslation(['property'])
  const { startOrder, completionDrawer } = useStartOrderForProperty()

  const { propertyId, resolvedWorkspaceId, handleClickOnEmptyAction } = useClientProperty()
  const canUserOrder = useCanUserOrder()

  const getPropertyOrdersQuery = useGetPropertyOrders(propertyId, resolvedWorkspaceId)
  const { isPending } = getPropertyOrdersQuery
  const propertyOrdersData = useMemo(() => getPropertyOrdersQuery.data?.data?.filter(it => !!it.assignments.length) ?? [], [getPropertyOrdersQuery.data])

  return (
    <Stack paddingTop="3.2rem">

      {isPending &&
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '20rem', width: '100%' }}>
          <CircularProgress
            sx={{
              color: Color.GRAY_DISABLED,
            }}
          />
        </Stack>
      }

      {!isPending &&
        <Grid container spacing={2.5} alignItems="start" direction={{ xs: 'column', sm: 'row' }}>

          {/* NO ORDERS */}
          {propertyOrdersData.length === 0 &&
            <Box maxWidth="60rem" width="100%" margin="auto" marginTop="8rem">
              <EmptyContent
                title={t('no_property_order')}
                icon={<ShoppingCartOutlined fontSize='large' sx={{ color: GRAY_900 }} />}
                button={
                  canUserOrder && (
                    <MUIButton
                      sx={{ marginTop: '2.4rem' }}
                      onClick={() => {
                        handleClickOnEmptyAction(AnalyticsEvent.PROPERTY_ORDER_VISUALS_CLICKED)
                        startOrder()
                      }}
                    >
                      {t('no_property_order_action')}
                    </MUIButton>
                  )
                }
              />
            </Box>
          }

          {propertyOrdersData.length > 0 &&
            <>
              {/* ORDERS SUMMARY CARD & DETAILS */}
              <Grid item xs={12} md={4} width="100%">
                <ClientPropertyPriceSummaryCard orders={propertyOrdersData} />
              </Grid>

              <Grid item xs={12} md={8} width="100%">
                {propertyOrdersData.map((order) => (

                  <GalleryAssignmentContextProvider assignmentId={order.assignments[0].id} key={order.id}>
                    <GalleryDealContextProvider assignmentId={order.assignments[0].id} keepPreviousData={true}>
                      <RescheduleOrderModuleProvider orderId={order.id}>

                        <Stack key={order.id} gap={2}>
                          <ClientGalleryOrderInformation context={OrderInformationContext.PROPERTY} />
                        </Stack>

                      </RescheduleOrderModuleProvider>
                    </GalleryDealContextProvider>
                  </GalleryAssignmentContextProvider>

                ))}
              </Grid>
            </>
          }

        </Grid>
      }

      {completionDrawer}

    </Stack>
  )
}
